import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { login } from "../../Services/loginService";
import * as Utils from "../../Utils";
import { UserContext } from "../../Context";
import { HOME, RESET_PASSWORD_REQUEST, SIGNUP } from "../../routes";
import { MESSAGES } from "./Constants";
import { PinInput, Spinner } from "../../Components/Common";
import { loginpage } from "../../Assets";


const LoginForm = () => {
  const [error, setError] = useState("");
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const userSchema = Yup.object({
    mobile: Yup.string()
      .matches(/^\d{10}$/, MESSAGES.MOBILE_VALIDATION)
      .required(MESSAGES.REQUIRED),
    password: Yup.string()
      .matches(/^\d{4}$/, MESSAGES.PIN_VALIDATION)
      .required(MESSAGES.REQUIRED),
  });

  const clearForm = (values) => {
    values.mobile = "";
    values.password = "";
  };

  const onSubmit = (values, { setSubmitting }) => {
    login(values)
      .then((response) => {
        if (response.status === 200 && response.data) {
          Utils.setUser(response.data);
          setUser(response.data);
          navigate(HOME, { replace: true });
        } else if (response.error) {
          const errorMsg = response.error.errors.error[0];
          setError(errorMsg);
          clearForm(values);
          toast.error(errorMsg || MESSAGES.LOGIN_FAILURE);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="min-h-screen flex items-center justify-center md:justify-end relative">
      <div
        className="absolute inset-0 opacity-80 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginpage})` }}
      />

      {/* Content (Header + Form) */}
      <div className="flex flex-col md:mr-24 gap-8 items-center relative z-10">
        {/* Big Header Text */}
        <p className="text-white z-50 text-5xl md:text-8xl font-bold">
          {process.env.REACT_APP_TITLE}
        </p>

        {/* Login form container */}
        <div className="bg-white/40 backdrop-blur-md p-8 rounded-2xl shadow-lg w-full max-w-md">
          {/* Company Name */}
          <div className="flex flex-col items-center">
            <p className="text-center text-2xl text-primary mt-4 font-semibold">
              LOGIN
            </p>
          </div>

          {/* Form */}
          <Formik
            initialValues={{ mobile: "", password: "" }}
            validationSchema={userSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form
                className="flex flex-col gap-4 w-full text-green"
                autoComplete="off"
              >
                {/* Mobile Number Field */}
                <div className="w-full font-semibold">
                  <label className="text-gray-600">Mobile</label>
                  <Field name="mobile">
                    {({ field }) => (
                      <input
                        {...field}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Enter your mobile number"
                        onChange={(e) => {
                          field.onChange(e);
                          setError("");
                        }}
                        className="w-full py-3 px-3 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-green"
                      />
                    )}
                  </Field>
                  <ErrorMessage name="mobile" component="p" className="text-red-500 text-xs italic" />
                </div>

                {/* PIN Input Field */}
                <div className="flex items-end justify-between">
                  <div>
                    <PinInput name="password" label="PIN" />

                    {/* Error Message */}
                    {error && (
                      <p className="text-red-500 italic text-center text-sm">
                        {error}
                      </p>
                    )}
                  </div>
                  <span>
                    <Link to={RESET_PASSWORD_REQUEST} className="text-sm text-gray-600 hover:underline">
                      Forgot / Reset PIN?
                    </Link>
                  </span>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center bg-primary text-white p-2 rounded"
                >
                  {isSubmitting ? <Spinner /> : "Log In"}
                </button>

                {/* Signup Link */}
                <div className="flex items-center gap-2 justify-center text-center mt-2 text-sm text-gray-600">
                  <span>Don't have an account?</span>
                  <Link
                    to={SIGNUP}
                    className="font-semibold text-gray-600 hover:underline"
                  >
                    Create an Account!
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
